import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { BottomSheet } from '@betty/navigation';
import { openDeposit } from '@betty/wallet/state';
import { selectWelcomePromos } from '@betty/promos';
import { Box, Button, Text } from '@betty/moonlight';
import { openChat } from '@betty/chat/state';
import {
  OnboardingAction,
  OnboardingBottomSheetProps,
} from './OnboardingBottomSheet.types';
import { ACTIONS } from './OnboardingBottomSheet.content';
import { OnboardingStep } from '../../state/onboarding.types';
import useOnfido from '../../hooks/useOnfido';
import BonusInfo from '../../components/BonusInfo/BonusInfo';

function OnboardingBottomSheet({ step, close }: OnboardingBottomSheetProps) {
  const dispatch = useDispatch();
  const onfido = useOnfido();
  const promos = useSelector(selectWelcomePromos);
  const hideBonus =
    step === OnboardingStep.SECURITY_REVIEW ||
    step === OnboardingStep.SECURITY_REVIEW_MANUAL ||
    step === OnboardingStep.REASSESSMENT;

  const navigate = useCallback(() => {
    const action = ACTIONS[step];

    switch (action) {
      case OnboardingAction.OPEN_CHAT:
        dispatch(openChat());

        break;
      case OnboardingAction.OPEN_DEPOSIT:
        dispatch(openDeposit());

        close();

        break;
      case OnboardingAction.OPEN_ONFIDO:
        close();

        BottomSheet.open({
          namespace: 'onboarding',
          type: 'OnfidoEntry',
        });

        break;
    }
  }, [step, dispatch, close]);

  return (
    <Box gap="space-400">
      {hideBonus ? null : (
        <Box
          backgroundColor="screenBackground"
          borderRadius="radius-200"
          paddingVertical="space-400"
          marginTop="space-400"
        >
          <BonusInfo
            promos={promos}
            imageSize={80}
            justifyContent="center"
            gap="space-300"
          />
        </Box>
      )}
      <Text
        variant="body-m"
        color="darkSurfacePrimary"
        locKey={`${step}Description`}
        locNS="onboarding"
      />
      <Box
        paddingHorizontal="space-200"
        flexDirection="row"
        alignSelf="flex-end"
        gap="space-200"
      >
        <Button
          variant="secondary"
          locKey="close"
          locNS="common"
          onPress={close}
        />
        <Button
          variant="primary"
          locKey={`${step}Button`}
          locNS="onboarding"
          loading={onfido.initializing}
          onPress={navigate}
        />
      </Box>
    </Box>
  );
}

export default memo(OnboardingBottomSheet);
